import { FunctionsHttpError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { PrecoIa, UsoIa } from '../utils/custoIa'

// ---------------------------------------------------------------------------
// Erros: o banco é quem barra; aqui só traduzimos para o usuário.
// ---------------------------------------------------------------------------

type ErroPg = { message: string; code?: string } | null

function falhar(e: ErroPg, repetido = 'Já existe um registro com esse valor'): void {
  if (!e) return
  if (e.code === '23505') throw new Error(repetido)
  if (e.code === '23503') throw new Error('Está em uso e não pode ser apagado')
  if (e.code === '23514' && /contas_id_externo_formato/.test(e.message)) throw new Error('O ID não está no formato desta plataforma — use o "Buscar" ou confira o exemplo')
  if (/permission denied/i.test(e.message)) throw new Error('Você não tem permissão para esta alteração')
  // As travas do banco (escalada, último Admin) já escrevem em português.
  throw new Error(e.message)
}

/**
 * Quando o RLS barra um update ou delete, o Supabase não dá erro: só altera zero linhas.
 * Sem esta conferência a tela acharia que salvou.
 */
function exigirLinhas(linhas: unknown[] | null) {
  if (!linhas?.length) throw new Error('Você não tem permissão para esta alteração')
}

// ---------------------------------------------------------------------------
// Marcas e contas
// ---------------------------------------------------------------------------

export type MarcaAdmin = { id: string; slug: string; nome: string; ativa: boolean }

export type ContaAdmin = {
  id: string
  plataforma: string
  idExterno: string
  nome: string
  moeda: string | null
  ativa: boolean
  demo: boolean
}

/** Slug a partir do nome: minúsculas, sem acento, hífen no lugar do resto. */
export function slugDe(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    // O banco aceita até 60 (criar_marca); corta sem deixar hífen no fim.
    .slice(0, 60)
    .replace(/-+$/, '')
}

export async function listarMarcas(): Promise<MarcaAdmin[]> {
  // Quem gerencia marcas vê também as desativadas (a policy deixa).
  const { data, error } = await supabase.from('marcas').select('id, slug, nome, ativa').order('nome')
  falhar(error)
  return (data ?? []) as MarcaAdmin[]
}

/** Pela função do banco: quem ativa marcas (sem ver todas) passa a enxergar a que criou. */
export async function criarMarca(nome: string): Promise<string> {
  const slug = slugDe(nome)
  if (!slug) throw new Error('Nome inválido')
  const { data, error } = await supabase.rpc('criar_marca', { p_nome: nome.trim(), p_slug: slug })
  falhar(error, `Já existe uma marca com o identificador "${slug}"`)
  return data as string
}

export async function atualizarMarca(id: string, mudanca: { nome?: string; ativa?: boolean }) {
  const { data, error } = await supabase.from('marcas').update(mudanca).eq('id', id).select('id')
  falhar(error)
  exigirLinhas(data)
}

export async function listarContas(marcaId: string): Promise<ContaAdmin[]> {
  const { data, error } = await supabase
    .from('contas')
    .select('id, plataforma, id_externo, nome, moeda, ativa, demo')
    .eq('marca_id', marcaId)
    .order('demo')
    .order('plataforma')
    .order('nome')
  falhar(error)
  return (data ?? []).map((c) => ({
    id: c.id,
    plataforma: c.plataforma,
    idExterno: c.id_externo,
    nome: c.nome,
    moeda: c.moeda,
    ativa: c.ativa,
    demo: c.demo,
  }))
}

/**
 * Como cada plataforma identifica uma conta. `normalizar` aceita o que a pessoa costuma
 * copiar da plataforma (ex.: o ID do Google Ads com traços) e devolve o formato do banco.
 */
export const FORMATO_ID: Record<string, { exemplo: string; ajuda: string; normalizar: (v: string) => string | null }> = {
  meta_ads: {
    exemplo: 'act_1234567890',
    ajuda: 'ID da conta de anúncios (Gerenciador de Negócios). Pode colar só os números.',
    normalizar: (v) => {
      const n = v.trim().replace(/^act_/i, '')
      return /^[1-9]\d{4,}$/.test(n) ? `act_${n}` : null
    },
  },
  google_ads: {
    exemplo: '123-456-7890',
    ajuda: 'ID do cliente no Google Ads (canto superior direito). Com ou sem traços.',
    normalizar: (v) => {
      const n = v.replace(/[\s-]/g, '')
      return /^\d{10}$/.test(n) ? n : null
    },
  },
  ga4: {
    exemplo: '123456789',
    ajuda: 'ID numérico da propriedade (GA4 → Administrador → Detalhes da propriedade).',
    normalizar: (v) => (/^[1-9]\d{5,}$/.test(v.trim()) ? v.trim() : null),
  },
  search_console: {
    exemplo: 'sc-domain:marca.com.br',
    ajuda: 'Propriedade de domínio (sc-domain:...) ou de prefixo de URL (https://...).',
    normalizar: (v) => {
      const n = v.trim()
      // O mesmo formato que o banco aceita: domínio em minúsculas; prefixo de URL com a barra depois do host.
      const dominio = /^sc-domain:([a-z0-9.-]+\.[a-z]{2,})$/i.exec(n)
      if (dominio) return `sc-domain:${dominio[1].toLowerCase()}`
      const url = /^(https?):\/\/([a-z0-9.-]+\.[a-z]{2,}(?::\d+)?)(\/\S*)?$/i.exec(n)
      return url ? `${url[1].toLowerCase()}://${url[2].toLowerCase()}${url[3] ?? '/'}` : null
    },
  },
  instagram: {
    exemplo: '17841400000000000',
    ajuda: 'ID da conta profissional do Instagram (vinculada à página do Facebook). Use o "Buscar no Meta".',
    normalizar: (v) => (/^[1-9]\d{4,}$/.test(v.trim()) ? v.trim() : null),
  },
  facebook: {
    exemplo: '100000000000000',
    ajuda: 'ID da página do Facebook.',
    normalizar: (v) => (/^[1-9]\d{4,}$/.test(v.trim()) ? v.trim() : null),
  },
  tiktok: {
    exemplo: '@marca',
    ajuda: 'Conta Business do TikTok (@ ou ID).',
    normalizar: (v) => (v.trim() ? v.trim() : null),
  },
}

/** Devolve o id da conta cadastrada (para já disparar a primeira coleta). */
export async function cadastrarConta(marcaId: string, plataforma: string, idExterno: string, nome: string): Promise<string> {
  const formato = FORMATO_ID[plataforma]
  const id = formato ? formato.normalizar(idExterno) : idExterno.trim()
  if (!id) throw new Error(`ID em formato inesperado. Exemplo: ${formato?.exemplo}`)
  const ads = plataforma === 'meta_ads' || plataforma === 'google_ads'
  const { data, error } = await supabase
    .from('contas')
    .insert({ marca_id: marcaId, plataforma, id_externo: id, nome: nome.trim(), moeda: ads ? 'BRL' : null })
    .select('id')
    .single()
  falhar(error, 'Essa conta já está cadastrada (nesta ou em outra marca)')
  return data!.id as string
}

/** Tira os dados fictícios de uma marca (quando ela passa a ter dado real). */
export async function removerDemonstracao(slug: string): Promise<number> {
  const { data, error } = await supabase.rpc('remover_dados_demo', { p_marca: slug })
  falhar(error)
  return Number(data ?? 0)
}

export async function atualizarConta(id: string, mudanca: { nome?: string; ativa?: boolean }) {
  const { data, error } = await supabase.from('contas').update(mudanca).eq('id', id).select('id')
  falhar(error)
  exigirLinhas(data)
}

// ---------------------------------------------------------------------------
// Coletas
// ---------------------------------------------------------------------------

/** Plataformas que já têm coletor. As outras mostram "coletor ainda não disponível". */
export const COLETORES: Record<string, string> = {
  meta_ads: 'coletar-meta-ads',
  instagram: 'coletar-meta-social',
  facebook: 'coletar-meta-social',
  google_ads: 'coletar-google-ads',
  ga4: 'coletar-google-organico',
  search_console: 'coletar-google-organico',
}

/** Até quantos dias cada coletor busca de uma vez (Instagram: uma chamada por dia). */
export const DIAS_MAXIMO_COLETA: Record<string, number> = {
  meta_ads: 90,
  google_ads: 90,
  ga4: 90,
  search_console: 90,
  instagram: 30,
  facebook: 30,
}

// ---------------------------------------------------------------------------
// Coleta diária (pg_cron no banco chama os coletores às 6h)
// ---------------------------------------------------------------------------

export type ColetaDiaria = { ativa: boolean; disponivel: boolean; horario: string; ultima: string | null; contas: number }

export async function situacaoColetaDiaria(): Promise<ColetaDiaria> {
  const { data, error } = await supabase.rpc('situacao_coleta_diaria')
  falhar(error)
  return data as ColetaDiaria
}

/** Ligar manda o endereço deste projeto: é para ele que o banco chama os coletores. */
export async function configurarColetaDiaria(ativa: boolean): Promise<ColetaDiaria> {
  const { data, error } = await supabase.rpc('configurar_coleta_diaria', {
    p_ativa: ativa,
    p_url: ativa ? String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '') : null,
  })
  falhar(error)
  return data as ColetaDiaria
}

export type Coleta = {
  status: 'rodando' | 'ok' | 'erro'
  iniciadaEm: string
  linhas: number | null
  mensagem: string | null
  periodoInicio: string | null
  periodoFim: string | null
}

/** A coleta mais recente de cada conta da marca. */
export async function ultimasColetas(marcaId: string): Promise<Map<string, Coleta>> {
  const { data, error } = await supabase
    .from('coletas')
    .select('conta_id, status, iniciada_em, linhas, mensagem, periodo_inicio, periodo_fim')
    .eq('marca_id', marcaId)
    .order('iniciada_em', { ascending: false })
    .limit(200)
  falhar(error)
  const porConta = new Map<string, Coleta>()
  for (const c of data ?? []) {
    if (porConta.has(c.conta_id)) continue
    porConta.set(c.conta_id, {
      status: c.status,
      iniciadaEm: c.iniciada_em,
      linhas: c.linhas,
      mensagem: c.mensagem,
      periodoInicio: c.periodo_inicio,
      periodoFim: c.periodo_fim,
    })
  }
  return porConta
}

export type ResultadoColeta = { status: 'ok' | 'erro'; linhas: number; mensagem: string | null }

/** Dispara o coletor da plataforma para uma conta, agora. */
export async function coletarAgora(plataforma: string, contaId: string, dias: number): Promise<ResultadoColeta> {
  const funcao = COLETORES[plataforma]
  if (!funcao) throw new Error('Esta plataforma ainda não tem coletor')
  const { data, error } = await supabase.functions.invoke(funcao, { body: { conta_id: contaId, dias } })
  if (error) await erroDaFuncao(error, 'Não foi possível coletar')
  const resultado = (data as { resultados: ResultadoColeta[] }).resultados[0]
  if (!resultado) throw new Error('O coletor não devolveu resultado')
  return resultado
}

// ---------------------------------------------------------------------------
// Usuários
// ---------------------------------------------------------------------------

export type UsuarioAdmin = {
  id: string
  email: string
  nome: string | null
  papelId: string | null
  ativo: boolean
  marcas: Set<string>
}

export async function listarUsuarios(): Promise<UsuarioAdmin[]> {
  const [u, um] = await Promise.all([
    supabase.from('usuarios').select('id, email, nome, papel_id, ativo').order('email'),
    supabase.from('usuario_marcas').select('usuario_id, marca_id'),
  ])
  falhar(u.error)
  falhar(um.error)
  const porUsuario = new Map<string, Set<string>>()
  for (const v of um.data ?? []) {
    porUsuario.set(v.usuario_id, (porUsuario.get(v.usuario_id) ?? new Set()).add(v.marca_id))
  }
  return (u.data ?? []).map((x) => ({
    id: x.id,
    email: x.email,
    nome: x.nome,
    papelId: x.papel_id,
    ativo: x.ativo,
    marcas: porUsuario.get(x.id) ?? new Set(),
  }))
}

export async function atualizarUsuario(id: string, mudanca: { nome?: string | null; papel_id?: string | null; ativo?: boolean }) {
  const { data, error } = await supabase.from('usuarios').update(mudanca).eq('id', id).select('id')
  falhar(error)
  exigirLinhas(data)
}

export async function liberarMarca(usuarioId: string, marcaId: string) {
  const { error } = await supabase.from('usuario_marcas').insert({ usuario_id: usuarioId, marca_id: marcaId })
  falhar(error)
}

export async function retirarMarca(usuarioId: string, marcaId: string) {
  const { data, error } = await supabase
    .from('usuario_marcas')
    .delete()
    .eq('usuario_id', usuarioId)
    .eq('marca_id', marcaId)
    .select('usuario_id')
  falhar(error)
  exigirLinhas(data)
}

/** As funções devolvem { erro } em português; o supabase-js embrulha num FunctionsHttpError. */
async function erroDaFuncao(error: Error, padrao: string): Promise<never> {
  if (error instanceof FunctionsHttpError) {
    const corpo = await error.context.json().catch(() => null)
    throw new Error(corpo?.erro ?? padrao)
  }
  throw new Error(error.message)
}

/** Cria no Auth pela Edge Function (a chave de serviço não pode ir para o navegador). */
export async function criarUsuario(email: string, senha: string, nome: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke('criar-usuario', { body: { email, senha, nome } })
  if (error) await erroDaFuncao(error, 'Não foi possível criar o usuário')
  return (data as { id: string }).id
}

/** Redefine a senha de outra pessoa (Edge Function); ela terá de trocar no próximo acesso. */
export async function redefinirSenha(usuarioId: string, senha: string): Promise<void> {
  const { error } = await supabase.functions.invoke('redefinir-senha', { body: { usuario_id: usuarioId, senha } })
  if (error) await erroDaFuncao(error, 'Não foi possível redefinir a senha')
}

/** Senha provisória legível: sem 0/O e 1/l para não confundir ao ditar. */
export function gerarSenhaProvisoria(tamanho = 12): string {
  const letras = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const aleatorio = crypto.getRandomValues(new Uint32Array(tamanho))
  return Array.from(aleatorio, (n) => letras[n % letras.length]).join('')
}

// ---------------------------------------------------------------------------
// Papéis e permissões
// ---------------------------------------------------------------------------

export type Permissao = { codigo: string; descricao: string }
export type PapelAdmin = { id: string; nome: string; descricao: string | null; sistema: boolean; permissoes: Set<string> }

export async function listarPapeis(): Promise<{ papeis: PapelAdmin[]; permissoes: Permissao[] }> {
  const [p, pp, cat] = await Promise.all([
    supabase.from('papeis').select('id, nome, descricao, sistema').order('sistema', { ascending: false }).order('nome'),
    supabase.from('papel_permissoes').select('papel_id, permissao'),
    supabase.from('permissoes').select('codigo, descricao').order('codigo'),
  ])
  falhar(p.error)
  falhar(pp.error)
  falhar(cat.error)
  const porPapel = new Map<string, Set<string>>()
  for (const v of pp.data ?? []) porPapel.set(v.papel_id, (porPapel.get(v.papel_id) ?? new Set()).add(v.permissao))
  return {
    papeis: (p.data ?? []).map((x) => ({ ...x, permissoes: porPapel.get(x.id) ?? new Set() })) as PapelAdmin[],
    permissoes: (cat.data ?? []) as Permissao[],
  }
}

export async function criarPapel(nome: string, descricao: string) {
  const { error } = await supabase.from('papeis').insert({ nome: nome.trim(), descricao: descricao.trim() || null })
  falhar(error, 'Já existe um papel com esse nome')
}

export async function atualizarPapel(id: string, mudanca: { nome?: string; descricao?: string | null }) {
  const { data, error } = await supabase.from('papeis').update(mudanca).eq('id', id).select('id')
  falhar(error, 'Já existe um papel com esse nome')
  exigirLinhas(data)
}

export async function apagarPapel(id: string) {
  const { data, error } = await supabase.from('papeis').delete().eq('id', id).select('id')
  if (error?.code === '23503') throw new Error('Há usuários com este papel. Troque o papel deles antes de apagar.')
  falhar(error)
  exigirLinhas(data)
}

export async function concederPermissao(papelId: string, permissao: string) {
  const { error } = await supabase.from('papel_permissoes').insert({ papel_id: papelId, permissao })
  falhar(error)
}

export async function revogarPermissao(papelId: string, permissao: string) {
  const { data, error } = await supabase
    .from('papel_permissoes')
    .delete()
    .eq('papel_id', papelId)
    .eq('permissao', permissao)
    .select('papel_id')
  falhar(error)
  exigirLinhas(data)
}

// ---------------------------------------------------------------------------
// IA (provedor, modelo e chaves). A chave só vai: o banco nunca a devolve.
// ---------------------------------------------------------------------------

export type ProvedorIa = 'anthropic' | 'openai' | 'google' | 'qwen' | 'groq'

/** O mesmo catálogo de supabase/functions/_shared/ia.ts (e de provedor_ia_valido() no banco). */
export const PROVEDORES_IA: Array<{
  id: ProvedorIa
  nome: string
  produto: string
  modeloPadrao: string
  sugestoes: string[]
  ondeCriar: string
  /** Onde ver o valor cobrado de verdade. */
  ondeVerGasto: string
  dica: string
}> = [
  {
    id: 'anthropic',
    nome: 'Anthropic',
    produto: 'Claude',
    modeloPadrao: 'claude-opus-5',
    sugestoes: ['claude-opus-5', 'claude-sonnet-5', 'claude-haiku-4-5'],
    ondeCriar: 'https://console.anthropic.com/settings/keys',
    ondeVerGasto: 'https://console.anthropic.com/usage',
    dica: 'No console da Anthropic, em Settings → API Keys. A conta precisa ter crédito (Settings → Billing).',
  },
  {
    id: 'openai',
    nome: 'OpenAI',
    produto: 'GPT',
    modeloPadrao: 'gpt-5',
    sugestoes: ['gpt-5', 'gpt-5-mini'],
    ondeCriar: 'https://platform.openai.com/api-keys',
    ondeVerGasto: 'https://platform.openai.com/usage',
    dica: 'Na plataforma da OpenAI, em API keys. A conta precisa ter crédito (Billing).',
  },
  {
    id: 'google',
    nome: 'Google',
    produto: 'Gemini',
    modeloPadrao: 'gemini-2.5-flash',
    sugestoes: ['gemini-2.5-flash', 'gemini-2.5-pro'],
    ondeCriar: 'https://aistudio.google.com/apikey',
    ondeVerGasto: 'https://console.cloud.google.com/billing',
    dica: 'No Google AI Studio, em Get API key.',
  },
  {
    id: 'qwen',
    nome: 'Alibaba Cloud',
    produto: 'Qwen',
    modeloPadrao: 'qwen-plus',
    sugestoes: ['qwen-plus', 'qwen-max', 'qwen-turbo'],
    ondeCriar: 'https://modelstudio.console.alibabacloud.com/',
    ondeVerGasto: 'https://modelstudio.console.alibabacloud.com/',
    dica: 'No Model Studio da Alibaba Cloud, na região internacional (Singapura) — chave de outra região não funciona.',
  },
  {
    id: 'groq',
    nome: 'Groq',
    produto: 'Llama',
    modeloPadrao: 'llama-3.3-70b-versatile',
    sugestoes: ['llama-3.3-70b-versatile', 'meta-llama/llama-4-maverick-17b-128e-instruct', 'llama-3.1-8b-instant'],
    ondeCriar: 'https://console.groq.com/keys',
    ondeVerGasto: 'https://console.groq.com/',
    dica: 'No console da Groq, em API Keys. Tem plano gratuito, com limite de uso por minuto e por dia.',
  },
]

export type SituacaoIa = {
  provedor: ProvedorIa
  modelo: string | null
  chaves: Array<{ provedor: ProvedorIa; final: string; atualizada_em: string }>
}

export async function carregarIa(): Promise<SituacaoIa> {
  const { data, error } = await supabase.rpc('ia_situacao')
  falhar(error)
  return data as SituacaoIa
}

/** Modelo null = o padrão do provedor. */
export async function usarProvedorIa(provedor: ProvedorIa, modelo: string | null) {
  const { data, error } = await supabase
    .from('ia_configuracao')
    .update({ provedor, modelo, atualizada_em: new Date().toISOString() })
    .eq('id', true)
    .select('provedor')
  if (error?.code === '23514') throw new Error('Nome de modelo inválido: use só letras, números, ponto, hífen e barra')
  falhar(error)
  exigirLinhas(data)
}

// Uso e custo estimado (ia_uso, ia_precos)

export async function usoIa(intervalo: { inicio: string; fim: string }): Promise<UsoIa[]> {
  const { data, error } = await supabase.rpc('uso_ia', { p_inicio: intervalo.inicio, p_fim: intervalo.fim })
  if (error) throw new Error(error.message)
  return ((data ?? []) as Array<Record<string, unknown>>).map((l) => ({
    provedor: String(l.provedor),
    modelo: String(l.modelo),
    origem: l.origem as UsoIa['origem'],
    chamadas: Number(l.chamadas),
    tokensEntrada: Number(l.tokens_entrada),
    tokensSaida: Number(l.tokens_saida),
  }))
}

export async function listarPrecosIa(): Promise<PrecoIa[]> {
  const { data, error } = await supabase.from('ia_precos').select('provedor, modelo, entrada, saida, fonte, conferido_em')
  if (error) throw new Error(error.message)
  return (data ?? []).map((p) => ({
    provedor: p.provedor,
    modelo: p.modelo,
    entrada: Number(p.entrada),
    saida: Number(p.saida),
    fonte: p.fonte,
    conferidoEm: p.conferido_em,
  }))
}

/** Cadastra ou troca o preço de um modelo (US$ por milhão de tokens). */
export async function salvarPrecoIa(p: { provedor: string; modelo: string; entrada: number; saida: number; fonte: string | null; conferidoEm: string | null }): Promise<void> {
  const { data, error } = await supabase
    .from('ia_precos')
    .upsert({ provedor: p.provedor, modelo: p.modelo, entrada: p.entrada, saida: p.saida, fonte: p.fonte, conferido_em: p.conferidoEm, atualizado_em: new Date().toISOString() })
    .select('modelo')
  if (error) throw new Error(/check constraint/i.test(error.message) ? 'Preço inválido (use números positivos; a fonte precisa começar com https://)' : error.message)
  if (!data?.length) throw new Error('Você não tem permissão para alterar os preços')
}

/** Chave vazia remove. */
export async function salvarChaveIa(provedor: ProvedorIa, chave: string) {
  const { error } = await supabase.rpc('definir_chave_ia', { p_provedor: provedor, p_chave: chave })
  falhar(error)
}

/** `modelos`: o que a chave enxerga, segundo o provedor (null = ele não respondeu). */
export type TesteIa = { provedor: string; modelo: string; modelos: string[] | null } & (
  | { ok: true; exemplo: string; conferida: boolean }
  | { ok: false; motivo: string }
)

/** Uma leitura de exemplo com a configuração salva (chamada real, custa alguns centavos). */
export async function testarIa(): Promise<TesteIa> {
  const { data, error } = await supabase.functions.invoke('leitura-ia', { body: { testar: true } })
  if (error) await erroDaFuncao(error, 'Não foi possível testar a IA')
  return data as TesteIa
}

// ---------------------------------------------------------------------------
// Credenciais das plataformas (só se existem — o valor nunca sai do servidor)
// ---------------------------------------------------------------------------

export type Credencial = { ok: boolean; falta: string[]; observacao?: string }
export type GrupoCredencial = 'meta' | 'google_ads' | 'google_organico' | 'ia' | 'email'
export type SituacaoCredenciais = Record<GrupoCredencial, Credencial> & {
  plataformas: Record<string, GrupoCredencial | null>
  /** O que o dono de uma marca precisa liberar (não são segredos). */
  ativacao?: { emailContaServico: string | null; mcc: string | null }
}

export const NOME_CREDENCIAL: Record<GrupoCredencial, string> = {
  meta: 'Meta (anúncios, Instagram e Facebook)',
  google_ads: 'Google Ads',
  google_organico: 'Google Analytics e Search Console',
  ia: 'IA',
  email: 'E-mail (Resend)',
}

// Páginas, Instagrams e contas de anúncio que o usuário do sistema do Meta enxerga (contas-meta)
export type AtivosMeta = {
  paginas: Array<{ id: string; nome: string; instagram: { id: string; usuario: string | null } | null }>
  contasAnuncio: Array<{ id: string; nome: string }>
  /** Os portfólios do usuário do sistema (o ID que o dono da marca usa para compartilhar). */
  portfolios?: Array<{ id: string; nome: string }>
  avisos: string[]
  /** ID → marca em que já está cadastrado. */
  cadastradas?: Record<string, string>
}

export async function ativosMeta(): Promise<AtivosMeta> {
  const { data, error } = await supabase.functions.invoke('contas-meta', { body: {} })
  if (error) await erroDaFuncao(error, 'Não foi possível consultar o Meta')
  return data as AtivosMeta
}

// O que as credenciais do Google enxergam (contas-google): GA4, Search Console e contas da MCC
export type AtivosGoogle = {
  ga4: Array<{ id: string; nome: string; conta: string }>
  searchConsole: Array<{ id: string; permissao: string }>
  googleAds: Array<{ id: string; nome: string; moeda: string }>
  avisos: string[]
  /** ID → marca em que já está cadastrado. */
  cadastradas?: Record<string, string>
}

export async function ativosGoogle(): Promise<AtivosGoogle> {
  const { data, error } = await supabase.functions.invoke('contas-google', { body: {} })
  if (error) await erroDaFuncao(error, 'Não foi possível consultar o Google')
  return data as AtivosGoogle
}

export async function situacaoCredenciais(): Promise<SituacaoCredenciais> {
  const { data, error } = await supabase.functions.invoke('situacao-credenciais', { body: {} })
  if (error) await erroDaFuncao(error, 'Não foi possível ver as credenciais')
  return data as SituacaoCredenciais
}

// ---------------------------------------------------------------------------
// Exclusão definitiva de marca (só o Admin de sistema; a marca precisa estar desativada)
// ---------------------------------------------------------------------------

/** Se quem está logado é o Admin de sistema (nenhum outro papel exclui marca). */
export async function ehAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc('eh_admin')
  if (error) return false
  return data === true
}

/** Quantas linhas a marca tem em cada tabela — o que a exclusão vai apagar. */
export async function contarDadosMarca(marcaId: string): Promise<Record<string, number>> {
  const { data, error } = await supabase.rpc('contar_dados_marca', { p_marca: marcaId })
  falhar(error)
  return (data ?? {}) as Record<string, number>
}

/** Apaga a marca e tudo dela. `confirmacao` é o nome da marca, digitado. */
export async function excluirMarca(marcaId: string, confirmacao: string): Promise<Record<string, number>> {
  const { data, error } = await supabase.rpc('excluir_marca', { p_marca: marcaId, p_confirmacao: confirmacao })
  falhar(error)
  return (data ?? {}) as Record<string, number>
}

/** As tabelas que mais dizem algo a quem confirma; o resto entra como "outros registros". */
export const ROTULOS_DADOS: Record<string, string> = {
  contas: 'contas conectadas',
  campanhas: 'campanhas',
  anuncios: 'anúncios',
  ads_desempenho_diario: 'dias de dados de campanha',
  ga4_canais_diario: 'dias de dados do GA4',
  gsc_diario: 'dias de dados do Search Console',
  social_publicacoes: 'publicações de redes sociais',
  leads: 'leads',
  conversoes: 'conversões configuradas',
  usuario_marcas: 'acessos de usuários',
  resumos: 'resumos e relatórios',
  relatorios_config: 'destinatários de relatórios',
  relatorios_envios: 'envios de relatórios',
  alertas: 'alertas',
}
